import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiRequest } from '../utils/api'
import { Download, Clock } from 'lucide-react'
import PageHeader from '../components/admin/PageHeader'
import DataTable from '../components/admin/DataTable'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

function timeAgo(dateStr) {
  if (!dateStr) return '—'
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDays = Math.floor(diffHr / 24)
  if (diffDays < 30) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function activityColor(dateStr) {
  if (!dateStr) return 'text-gray-500'
  const diffDays = (Date.now() - new Date(dateStr)) / 86400000
  if (diffDays < 1) return 'text-emerald-600'
  if (diffDays < 7) return 'text-blue-600'
  if (diffDays < 30) return 'text-amber-600'
  return 'text-gray-500'
}

export default function Users() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [page, planFilter])

  const loadUsers = async () => {
    try {
      setLoading(true)
      let url = `/api/admin/users?page=${page}&limit=20`
      if (search) url += `&search=${encodeURIComponent(search)}`
      if (planFilter) url += `&plan=${planFilter}`
      
      const data = await apiRequest(url)
      setUsers(data)
    } catch (err) {
      console.error('Error loading users:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    loadUsers()
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const token = localStorage.getItem('admin_token')
      let url = `/api/admin/export/users?`
      if (planFilter) url += `plan=${planFilter}&`
      
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production',
        }
      })
      if (!res.ok) throw new Error('Export failed')
      
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `squadline_users_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      console.error('Export error:', err)
      alert('Failed to export users')
    } finally {
      setExporting(false)
    }
  }

  const columns = [
    {
      key: 'name',
      header: 'User',
      accessor: (row) => row.name,
      render: (row) => (
        <Link to={`/users/${row.user_id}`} className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            {row.picture ? (
              <img src={row.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <span className="text-indigo-600 font-medium">{row.name?.charAt(0) || '?'}</span>
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">{row.name}</p>
            {row.is_admin && (
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Admin</Badge>
            )}
          </div>
        </Link>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      accessor: 'email',
      render: (row) => <span className="text-gray-600">{row.email}</span>,
    },
    {
      key: 'plan',
      header: 'Plan',
      accessor: (row) => row.subscription_plan || 'free',
      render: (row) => {
        const plan = row.subscription_plan || 'free'
        const variant = plan === 'premium' ? 'bg-indigo-100 text-indigo-700' :
          plan === 'coach' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
        return <Badge className={variant}>{plan}</Badge>
      },
    },
    {
      key: 'teams_count',
      header: 'Teams',
      accessor: 'teams_count',
      render: (row) => <span className="text-gray-600">{row.teams_count}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.is_suspended ? 'Suspended' : 'Active',
      render: (row) => (
        <Badge className={row.is_suspended ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>
          {row.is_suspended ? 'Suspended' : 'Active'}
        </Badge>
      ),
    },
    {
      key: 'last_active',
      header: 'Last Active',
      accessor: 'last_active',
      render: (row) => (
        <span className={`flex items-center gap-1 text-sm ${activityColor(row.last_active)}`}>
          <Clock className="w-3.5 h-3.5" />
          {timeAgo(row.last_active)}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      accessor: 'created_at',
      render: (row) => (
        <span className="text-gray-500">
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Users" description="Manage all platform users">
        <Button onClick={handleExport} disabled={exporting}>
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </PageHeader>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <DataTable
            columns={[]}
            data={[]}
            searchable={false}
          />
        </form>
        <Select value={planFilter || 'all'} onValueChange={(val) => { setPlanFilter(val === 'all' ? '' : val); setPage(1); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Plans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
            <SelectItem value="coach">Coach</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={users}
          searchable
          searchPlaceholder="Search by name or email..."
          sortable
          onRowClick={(row) => window.location.href = `/users/${row.user_id}`}
          emptyMessage="No users found"
        />
      )}

      {/* Server-side Pagination */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
          Previous
        </Button>
        <span className="text-sm text-gray-500">Page {page}</span>
        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={users.length < 20}>
          Next
        </Button>
      </div>
    </div>
  )
}
